import { DetailedApiMethod } from '../../../types/types';
import { FormApiMethodsRequestData } from '../../../api/types/types';
import { apiRequest } from '../../../api/utils/apiRequest';
import { detailedApiMethodFetcher } from '../../../api/utils/fetchers/detailedApiMethodFetcher';
import useApiErrorHandling from '../../../hooks/useApiErrorHandling';
import { ResponseActionHandler } from '../../../types/context';

// Executes a DetailedApiMethod request with recursive force/entry error
// handling. Re-opens the modal for each sequential error, then returns
// { response, error: null } on success or { response: null, error } on failure.
export const submitWithForceRetry = async (
  resolvedMethod: DetailedApiMethod,
  submitRequestData: FormApiMethodsRequestData,
  handleApiError: ReturnType<typeof useApiErrorHandling>,
  getResponseActionHandler?: (name: string) => ResponseActionHandler | undefined
): Promise<{ response: unknown; error: Error | null }> => {
  // Recursive retry: passes itself as forceModalAction so any number of
  // sequential force/entry errors are handled without breaking the modal flow.
  const retryWithForce = async (
    forceHeader: string
  ): Promise<{ response: unknown; error: Error | null }> => {
    try {
      const res = await apiRequest(
        resolvedMethod,
        submitRequestData,
        (apiMethod) => detailedApiMethodFetcher(apiMethod, forceHeader),
        getResponseActionHandler
      );
      return { response: res, error: null };
    } catch (retryError) {
      const retryRes = await handleApiError(retryError, false, retryWithForce);
      if (retryRes instanceof Error) {
        return { response: null, error: retryRes };
      }
      return retryRes;
    }
  };

  try {
    const res = await apiRequest(
      resolvedMethod,
      submitRequestData,
      detailedApiMethodFetcher,
      getResponseActionHandler
    );
    return { response: res, error: null };
  } catch (e) {
    const errorHandlingRes = await handleApiError(e, false, retryWithForce);
    if (errorHandlingRes instanceof Error) {
      return { response: null, error: errorHandlingRes };
    }
    return errorHandlingRes;
  }
};
